import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { serviceZoneService } from '../../../services/serviceZoneService';

const ServiceAreaMap = () => {
  const [postalCode, setPostalCode] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serviceAreas, setServiceAreas] = useState([]);

  useEffect(() => {
    loadServiceAreas();
  }, []);

  const loadServiceAreas = async () => {
    try {
      const zones = await serviceZoneService?.getActiveZones();
      const uniqueAreas = [];
      const seenNames = new Set();

      zones?.forEach(zone => {
        if (zone?.municipalityName && !seenNames?.has(zone?.municipalityName)) {
          seenNames?.add(zone?.municipalityName);
          const postalPrefix = zone?.postalCode?.substring(0, 2);
          uniqueAreas?.push(`${zone?.municipalityName} (${postalPrefix})`);
        }
      });

      setServiceAreas(uniqueAreas?.slice(0, 8));
    } catch (error) {
      console.error('Error loading service areas:', error);
      setServiceAreas([
        'Paris (75)',
        'Hauts-de-Seine (92)',
        'Seine-Saint-Denis (93)',
        'Val-de-Marne (94)'
      ]);
    }
  };

  const handleVerifyPostalCode = async () => {
    if (!postalCode) return;
    
    setLoading(true);
    try {
      const isValid = await serviceZoneService?.validateLocation(
        postalCode,
        null,
        null,
        null
      );
      
      if (isValid) {
        setVerificationResult({
          available: true,
          message: 'Excellent ! Nous intervenons dans votre secteur.'
        });
      } else {
        setVerificationResult({
          available: false,
          message: 'Désolé, ce secteur n\'est pas encore couvert. Contactez-nous pour plus d\'informations.'
        });
      }
    } catch (error) {
      console.error('Postal code verification error:', error);
      setVerificationResult({
        available: false,
        message: 'Erreur lors de la vérification. Veuillez réessayer.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Zone d'intervention
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              Nous intervenons dans toute l'Île-de-France avec des temps de réponse optimisés.
            </p>
            
            <div className="bg-card border border-border rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-foreground mb-4">Vérifiez votre code postal</h3>
              <div className="flex gap-3">
                <Input
                  type="text"
                  placeholder="Ex: 75001"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e?.target?.value)}
                  maxLength={5}
                  className="flex-1"
                />
                <Button
                  variant="default"
                  iconName="Search"
                  onClick={handleVerifyPostalCode}
                  loading={loading}
                >
                  Vérifier
                </Button>
              </div>
              
              {verificationResult && (
                <div className={`mt-4 p-4 rounded-lg ${
                  verificationResult?.available 
                    ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                }`}>
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={verificationResult?.available ? 'CheckCircle2' : 'AlertCircle'} 
                      size={20} 
                    />
                    <span className="font-medium">{verificationResult?.message}</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {serviceAreas?.map((area, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                >
                  <Icon name="MapPin" size={16} className="text-primary" />
                  <span>{area}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="bg-card border border-border rounded-xl p-8 shadow-lg">
              <div className="aspect-square bg-muted/50 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Icon name="Map" size={80} className="text-primary/30 mb-4 mx-auto" />
                  <p className="text-muted-foreground">Carte de la zone d'intervention</p>
                  <p className="text-sm text-muted-foreground mt-2">Paris et Île-de-France</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreaMap;